/**
 * Fetch wrapper: prefixes /api/v1, attaches the bearer token, refreshes once on
 * 401 (single-flight across concurrent requests and StrictMode double effects),
 * and throws a normalised ProblemError for every failure.
 */
import { authStore } from "@/lib/auth"
import { currentLang } from "@/lib/i18n"
import { ProblemError } from "@/lib/problem"
import type { TokenResponse } from "@/lib/types"

const BASE = "/api/v1"

let refreshing: Promise<boolean> | null = null

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Requested-With": "fetch", "Accept-Language": currentLang() },
    })
    if (!res.ok) return false
    const body = (await res.json()) as TokenResponse
    authStore.setToken(body.access_token, body.user)
    return true
  } catch {
    return false
  }
}

/** One refresh at a time; concurrent callers share the same promise. */
export function refreshOnce(): Promise<boolean> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null
  })
  return refreshing
}

function redirectToLogin() {
  authStore.signOut()
  const here = window.location.pathname + window.location.search
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign(`/login?next=${encodeURIComponent(here)}&expired=1`)
  }
}

export type ApiInit = Omit<RequestInit, "body"> & { body?: unknown; retry?: boolean }

export async function apiRaw(path: string, init: ApiInit = {}): Promise<Response> {
  const { body, retry = true, headers, ...rest } = init
  const token = authStore.get().token
  const isForm = body instanceof FormData
  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      ...rest,
      credentials: "include",
      headers: {
        "Accept-Language": currentLang(),
        ...(body !== undefined && !isForm ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers as Record<string, string> | undefined),
      },
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    })
  } catch (err) {
    throw ProblemError.network(err)
  }
  if (res.status === 401 && retry && !path.startsWith("/auth/")) {
    if (await refreshOnce()) return apiRaw(path, { ...init, retry: false })
    redirectToLogin()
  }
  if (!res.ok) throw await ProblemError.from(res)
  return res
}

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  const res = await apiRaw(path, init)
  if (res.status === 204 || (res.status === 202 && res.headers.get("content-length") === "0")) {
    return undefined as T
  }
  return (await res.json()) as T
}

/** Authorised download of a file/CSV/XLSX, returned as a Blob. */
export async function apiBlob(path: string): Promise<Blob> {
  return (await apiRaw(path)).blob()
}

/** Save a blob with a filename (CSV / XLSX exports). */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ""
}
